import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };

function euro(value) {
  return Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function priorityColor(priority) {
  return priority === 'hoch' || priority === 'red' ? '#b42318' : priority === 'mittel' || priority === 'amber' ? '#a16207' : '#064734';
}

const labels = {
  anomalies: 'Anomalie',
  contracts: 'Vertrag',
  budgets: 'Budget',
  geldfresser: 'Geldfresser'
};

export default function ReviewPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setError('');
    try {
      const r = await api.get('/review/inbox');
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Review-Inbox konnte nicht geladen werden');
    }
  }

  async function decide(item, action) {
    setSaving(item.item_key + action);
    setError('');
    try {
      const payload = { item_key: item.item_key, quelle: item.quelle, aktion: action };
      if (action === 'notwendig') payload.empfaenger = item.titel;
      if (action === 'spaeter') {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        payload.snooze_until = d.toISOString().slice(0, 10);
      }
      await api.post('/review/decision', payload);
      setData(current => ({
        ...current,
        items: (current?.items || []).filter(row => row.item_key !== item.item_key),
        totals: { ...(current?.totals || {}), gesamt: Math.max(0, Number(current?.totals?.gesamt || 1) - 1) }
      }));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Entscheidung konnte nicht gespeichert werden');
    } finally {
      setSaving('');
    }
  }

  const items = data?.items || [];
  const filtered = useMemo(() => items.filter(item => filter === 'all' || item.quelle === filter || item.prioritaet === filter), [items, filter]);
  const totals = data?.totals || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#064734', fontSize: '25px' }}>Finanz-Review</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Eine Inbox für Geldfresser, Verträge, Budgets und Anomalien.</p>
        </div>
        <button onClick={load} style={button('#334155')}>Aktualisieren</button>
      </div>

      {error && <Notice>{error}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <Stat title="Offen" value={totals.gesamt || 0} hint={data?.monat || '-'} tone="blue" />
        <Stat title="Dringend" value={totals.hoch || 0} hint="hohe Priorität" tone="red" />
        <Stat title="Verträge" value={totals.contracts || 0} hint="Abo-Radar" tone="amber" />
        <Stat title="Volumen" value={euro(totals.betrag || 0)} hint="Prüfsumme" tone="slate" />
      </div>

      <section style={{ ...card, marginBottom: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'center' }}>
          <div>
            <h2 style={h2}>Heute entscheiden</h2>
            <p style={{ ...muted, margin: '4px 0 0' }}>Deine Entscheidungen werden gespeichert. Erledigte und ignorierte Punkte verschwinden aus der Inbox.</p>
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={input}>
            <option value="all">Alle Quellen</option>
            <option value="hoch">Hohe Priorität</option>
            <option value="anomalies">Anomalien</option>
            <option value="contracts">Verträge</option>
            <option value="budgets">Budgets</option>
            <option value="geldfresser">Geldfresser</option>
          </select>
        </div>
      </section>

      <div style={{ display: 'grid', gap: '12px' }}>
        {filtered.map(item => (
          <article key={item.item_key} style={{ ...card, borderColor: priorityColor(item.prioritaet) }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'start' }}>
              <div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '7px' }}>
                  <Badge>{labels[item.quelle] || item.quelle}</Badge>
                  <Badge tone={priorityColor(item.prioritaet)}>{item.prioritaet}</Badge>
                </div>
                <h2 style={{ margin: 0, color: '#12231d', fontSize: '18px' }}>{item.titel}</h2>
                <p style={{ ...muted, margin: '4px 0 0' }}>{item.kategorie || 'ohne Kategorie'} - {item.datum || data?.monat}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: priorityColor(item.prioritaet), fontSize: '20px', fontWeight: 950 }}>{euro(item.betrag)}</div>
                <div style={muted}>{item.suggested_action}</div>
              </div>
            </div>
            <p style={{ color: '#334155', fontWeight: 800 }}>{item.text}</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button disabled={saving} onClick={() => nav(item.detail_url)} style={button('#064734')}>Details prüfen</button>
              <button disabled={saving} onClick={() => decide(item, 'pruefen')} style={button('#0f766e')}>Prüfen</button>
              <button disabled={saving} onClick={() => decide(item, 'normal')} style={button('#475569')}>Ist normal</button>
              <button disabled={saving} onClick={() => decide(item, 'notwendig')} style={button('#15803d')}>Notwendig</button>
              <button disabled={saving} onClick={() => decide(item, 'spaeter')} style={button('#a16207')}>Später</button>
              <button disabled={saving} onClick={() => decide(item, 'ignorieren')} style={button('#7f1d1d')}>Ignorieren</button>
            </div>
          </article>
        ))}
        {!filtered.length && <div style={card}>Keine offenen Prüfpunkte für diesen Filter.</div>}
      </div>
    </div>
  );
}

function Stat({ title, value, hint, tone = 'slate' }) {
  const colors = { blue: '#1d4ed8', red: '#b42318', amber: '#a16207', slate: '#12231d' };
  return <div style={card}><div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div><div style={{ color: colors[tone] || colors.slate, fontSize: '24px', fontWeight: 950, marginTop: '6px' }}>{value}</div><div style={{ ...muted, marginTop: '3px' }}>{hint}</div></div>;
}

function Badge({ children, tone = '#064734' }) {
  return <span style={{ border: `1px solid ${tone}`, color: tone, borderRadius: '999px', padding: '3px 8px', fontSize: '11px', fontWeight: 950, textTransform: 'uppercase' }}>{children}</span>;
}

function Notice({ children }) {
  return <div style={{ background: '#fff1ef', color: '#9f1d12', border: '1px solid #ffc9c2', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

const h2 = { margin: 0, color: '#064734', fontSize: '17px' };
const input = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px 11px', background: 'white', color: '#12231d', fontWeight: 800 };
function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '9px 11px', cursor: 'pointer', fontWeight: 900, opacity: 1 };
}
